import api from './client';
import type {
  EmployeeFlowType,
  EmployeeFlowStatus,
  EmployeeFlow,
  AssignedTaskList,
  AssignedTask,
  AssignedTaskComment,
  AssignedTaskActivityLog,
  CreateEmployeeFlowPayload,
  AssignTaskListsPayload,
  CreateAdHocTaskPayload,
  UpdateAssignedTaskPayload,
  UpdateAssignedTaskStatusPayload,
  CreateAssignedTaskCommentPayload,
  UpdateAssignedTaskCommentPayload,
  ReorderPayload,
  ApiResponse,
  ManagerAssigneePreview,
} from '@gokrew/models/employee-boarding';

export async function createEmployeeFlow(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  payload: CreateEmployeeFlowPayload
): Promise<ApiResponse<EmployeeFlow>> {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/${type}/flows`,
    payload
  );
  return data;
}

export async function listEmployeeFlows(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  params?: { status?: EmployeeFlowStatus }
): Promise<ApiResponse<EmployeeFlow[]>> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/${type}/flows`,
    { params }
  );
  return data;
}

export async function getEmployeeFlow(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  flowId: string
): Promise<ApiResponse<EmployeeFlow>> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/${type}/flows/${flowId}`
  );
  return data;
}

export async function completeEmployeeFlow(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  flowId: string
): Promise<ApiResponse<EmployeeFlow>> {
  const { data } = await api.patch(
    `/companies/${companyId}/employees/${employeeId}/${type}/flows/${flowId}/complete`
  );
  return data;
}

export async function assignTaskListsToFlow(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  flowId: string,
  payload: AssignTaskListsPayload
): Promise<ApiResponse<AssignedTaskList[]>> {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/${type}/flows/${flowId}/task-lists`,
    payload
  );
  return data;
}

export async function createAdHocTask(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  listId: string,
  payload: CreateAdHocTaskPayload
): Promise<ApiResponse<AssignedTask>> {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/${type}/lists/${listId}/tasks`,
    payload
  );
  return data;
}

export async function getAssignedTask(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string
): Promise<ApiResponse<AssignedTask>> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}`
  );
  return data;
}

export async function updateAssignedTask(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string,
  payload: UpdateAssignedTaskPayload
): Promise<ApiResponse<AssignedTask>> {
  const { data } = await api.patch(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}`,
    payload
  );
  return data;
}

export async function deleteAssignedTask(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string
): Promise<ApiResponse<{ id: string }>> {
  const { data } = await api.delete(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}`
  );
  return data;
}

export async function updateAssignedTaskStatus(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string,
  payload: UpdateAssignedTaskStatusPayload
): Promise<ApiResponse<AssignedTask>> {
  const { data } = await api.patch(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}/status`,
    payload
  );
  return data;
}

export async function reorderTaskList(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  listId: string,
  payload: ReorderPayload
): Promise<ApiResponse<{ id: string; orderIndex: number }>> {
  const { data } = await api.patch(
    `/companies/${companyId}/employees/${employeeId}/${type}/lists/${listId}/order`,
    payload
  );
  return data;
}

export async function reorderTask(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string,
  payload: ReorderPayload
): Promise<ApiResponse<{ id: string; orderIndex: number }>> {
  const { data } = await api.patch(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}/order`,
    payload
  );
  return data;
}

export async function listTaskComments(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string
): Promise<ApiResponse<AssignedTaskComment[]>> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}/comments`
  );
  return data;
}

export async function addTaskComment(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string,
  payload: CreateAssignedTaskCommentPayload
): Promise<ApiResponse<AssignedTaskComment>> {
  const { data } = await api.post(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}/comments`,
    payload
  );
  return data;
}

export async function updateTaskComment(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string,
  commentId: string,
  payload: UpdateAssignedTaskCommentPayload
): Promise<ApiResponse<AssignedTaskComment>> {
  const { data } = await api.patch(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}/comments/${commentId}`,
    payload
  );
  return data;
}

export async function deleteTaskComment(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string,
  commentId: string
): Promise<ApiResponse<{ id: string }>> {
  const { data } = await api.delete(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}/comments/${commentId}`
  );
  return data;
}

/** Who "Their manager" steps would go to now (the start dialog, D191). */
export async function getManagerAssigneePreview(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType
): Promise<ApiResponse<ManagerAssigneePreview>> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/${type}/manager-assignees`
  );
  return data;
}

export async function listTaskActivityLogs(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  taskId: string
): Promise<ApiResponse<AssignedTaskActivityLog[]>> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/${type}/tasks/${taskId}/activity-logs`
  );
  return data;
}

export async function getListTasks(
  companyId: string,
  employeeId: string,
  type: EmployeeFlowType,
  listId: string,
  parentOnly = false
): Promise<ApiResponse<AssignedTask[]>> {
  const { data } = await api.get(
    `/companies/${companyId}/employees/${employeeId}/${type}/lists/${listId}/tasks`,
    { params: parentOnly ? { parentOnly: true } : undefined }
  );
  return data;
}
